import React, { useEffect, useState } from 'react';
import './styles.scss';
import {
  getStoredDrunkPersistent,
  getStoredHungerPersistent,
  HUD_PREFS_EVENT,
} from '../../theme/hudPrefs';

interface VitalsState {
  drunk: number; // 0-10
  hunger: number; // 0-10
}

const MAX_LEVEL = 10;
const LOW_HUNGER = 3;

// Segmented gauge for the two server-driven needs.
const Pips: React.FC<{ value: number; tone: string }> = ({ value, tone }) => (
  <div className={`vitals-pips vitals-pips--${tone}`} role="meter" aria-valuemin={0} aria-valuemax={MAX_LEVEL} aria-valuenow={value}>
    {Array.from({ length: MAX_LEVEL }, (_, i) => (
      <span key={i} className={'vitals-pips__pip' + (i < value ? ' vitals-pips__pip--on' : '')} />
    ))}
  </div>
);

// Small always-mounted HUD corner widget, driven by skymp5-client's
// vitalsService.ts. Health, magicka and stamina are the game's own bars; this
// widget only adds the two needs the server tracks. Each one is persistent by
// default and can be switched to "only when it matters" in the escape menu.
const Vitals: React.FC = () => {
  // Nothing is drawn until the server has sent a first value, so a fresh
  // connection never flashes a made-up gauge.
  const [state, setState] = useState<VitalsState | null>(null);
  const [hungerPersistent, setHungerPersistent] = useState<boolean>(getStoredHungerPersistent);
  const [drunkPersistent, setDrunkPersistent] = useState<boolean>(getStoredDrunkPersistent);

  useEffect(() => {
    const handler = (e: Event) => {
      const d = ((e as CustomEvent).detail || {}) as Partial<VitalsState>;
      setState((prev) => ({
        drunk: typeof d.drunk === 'number' ? d.drunk : prev ? prev.drunk : 0,
        hunger: typeof d.hunger === 'number' ? d.hunger : prev ? prev.hunger : MAX_LEVEL,
      }));
    };
    window.addEventListener('skymp5-client:vitalsState', handler);
    return () => window.removeEventListener('skymp5-client:vitalsState', handler);
  }, []);

  // The escape menu options page flips these preferences.
  useEffect(() => {
    const onPrefs = () => {
      setHungerPersistent(getStoredHungerPersistent());
      setDrunkPersistent(getStoredDrunkPersistent());
    };
    window.addEventListener(HUD_PREFS_EVENT, onPrefs);
    return () => window.removeEventListener(HUD_PREFS_EVENT, onPrefs);
  }, []);

  if (!state) return null;

  const showHunger = hungerPersistent || state.hunger < MAX_LEVEL;
  const showDrunk = drunkPersistent || state.drunk > 0;
  if (!showHunger && !showDrunk) return null;

  const lowHunger = state.hunger <= LOW_HUNGER;

  return (
    <div className="vitals-hud">
      <div className="vitals-needs">
        {showHunger && (
          <div className={'vitals-need' + (lowHunger ? ' vitals-need--low' : '')}>
            <span className="vitals-need__label">Faim</span>
            <Pips value={state.hunger} tone="hunger" />
          </div>
        )}
        {showDrunk && (
          <div className="vitals-need">
            <span className="vitals-need__label">Ivresse</span>
            <Pips value={state.drunk} tone="drunk" />
          </div>
        )}
      </div>
    </div>
  );
};

export default Vitals;
